// The executive module's words and pure helpers (ADR 0019; design §2.13 and §2.14 item 8; stage 6). DOM-free, so a test
// pins every sentence the pages say. What the page shows is what the routes return (src/server/routes/executive.ts, the
// contract): AI-made, unattributed text, the names of projects two or more people shared, and counts — never a member's
// name but the executives' own, never a meeting. Times are said in UTC with the zone, as the report's windows are
// defined (EXECUTIVE_REPORT_HOUR_UTC; 04-data §4: "always with a timezone").
import type {
  ExecutiveBackfillProgressDto,
  ExecutiveInsufficientDto,
  ExecutiveMatterDto,
  ExecutiveRefreshResponse,
  ExecutiveReportDto,
  ExecutiveReportResponse,
  ExecutiveReportRowDto,
  ExecutiveSignalCount,
  ExecutiveSlackBasisDto,
  ExecutiveWeekSignalCount,
} from '../../../shared/api-types.js';
import {
  CLIMATE_SIGNAL_LABELS,
  type ClimateSignal,
  type ExecutiveClimateUncounted,
  type ExecutiveMatterStatus,
  type ExecutiveReportCadence,
  type ExecutiveReportTrigger,
  type ExecutiveSlackUncounted,
  type ExecutiveWithheldReason,
} from '../../../shared/enums.js';
import { EM_DASH, formatCount, formatDate, formatDateTime, formatList, formatTime, pluralise } from '../../ui/format.js';

/** Every time on these pages is the report's own clock: UTC (the report hour, the windows, the cooldown). */
export const EXECUTIVE_TIME_ZONE = 'UTC';
const UTC = { timeZone: EXECUTIVE_TIME_ZONE };

// --- The author line (design §2.13 and §2.14 item 8), on every page of the module ------------------------------------

/**
 * Why two clauses differ from the design's sentence (the stage 6 brief: "check every clause against stage 5 … correct the
 * words if they would overstate"):
 * - "only a project two or more people share is ever named" → "…shared when the report was written…": a name is checked
 *   against the shares just before the report is stored and never again (the owner's ruling on ADR 0019's Open 18,
 *   "Re-check on writing"), so a stored report can name a project whose share has since ended. M2 asks, at that read,
 *   that the sharer and each recipient be admitted and the recipient hold project access (permission model 1.3.0 §9.1):
 *   a project with such a share is held by two or more people then, and only then.
 * - "dismiss the item" → "dismiss any matter that carries it": a theme has no control to dismiss it; a report's action
 *   becomes a shared matter, which each executive can dismiss for themselves.
 * - "Themes rest on at least three people’s meetings." → "…named, and only where at least three people’s meetings stand
 *   behind it. A theme may rest on one person’s meetings; its evidence says how many." Why (the owner's rulings of
 *   2026-10-12, "Remove them everywhere" and "Names only where three"): no theme needs three people any more — one
 *   person's meetings, an executive's own included ("Count them"), make a theme — and the one three-person rule left is
 *   permission model 1.3.0 §9.1 condition 5, a shared project's name only on a theme whose cited meetings are at least
 *   three people's (ai/executive/validate.ts `EXECUTIVE_NAME_MIN_CONTRIBUTORS`). The Evidence column's exact counts are
 *   how an executive tells a theme of one person's meetings from one of many.
 * Every other clause stands as written: M1 reads the AI-made summary, its key points, decisions and climate word, M4 the
 * wordless Slack marks; nothing sent names a member.
 */
export const EXECUTIVE_AUTHOR_LINE =
  'Written by Calis from members’ AI-made summaries, key points and decisions and the climate of their meetings and Slack — never a transcript, a message, a title or a name; only a project that two or more people shared when the report was written is ever named, and only where at least three people’s meetings stand behind it. A theme may rest on one person’s meetings; its evidence says how many. Check before acting; a person’s name that slipped through is a defect — dismiss any matter that carries it.';

/** Said once, on the shared list: a manual matter is an executive's own words, which the author line does not cover. */
export const MANUAL_MATTER_NOTE = 'A matter an executive added is in that executive’s own words, and every executive can see it.';

// --- States the module shares (directives §8; 03-components §1) ------------------------------------------------------

export const EXECUTIVE_OFF_TITLE = 'Executive intelligence is off on this server';
export const EXECUTIVE_OFF_DESCRIPTION = 'An operator can switch it on.';
export const EXECUTIVE_DENIED_WHAT = 'executive intelligence';

/** "6 Oct 2026, 06:04 UTC". */
export function utcDateTime(value: string): string {
  return formatDateTime(value, UTC);
}

/** "06:04 UTC" on the same UTC day as `now`, else "6 Oct 2026, 06:04 UTC". */
export function utcMoment(value: string, now: Date = new Date()): string {
  const date = new Date(value);
  const sameDay = date.toISOString().slice(0, 10) === now.toISOString().slice(0, 10);
  return sameDay ? `${formatTime(value, UTC)} UTC` : utcDateTime(value);
}

/** "Tuesday 6 October 2026" in UTC — the long form names a report (an absolute, unambiguous date, 04-data §4). */
export function longUtcDate(value: string): string {
  // Why parts from `en`: en-GB inserts a comma after the weekday and recent ICU data shortens September; the assembled form
  // is stable across runtimes (format.ts's own reasoning for its short date).
  const parts = new Intl.DateTimeFormat('en', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: EXECUTIVE_TIME_ZONE }).formatToParts(new Date(value));
  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')} ${get('year')}`;
}

/** "5 Oct 2026, 06:00 to 6 Oct 2026, 06:00 UTC" — a report's window, both ends absolute. */
export function windowText(start: string, end: string): string {
  return `${formatDate(start, UTC)}, ${formatTime(start, UTC)} to ${formatDate(end, UTC)}, ${formatTime(end, UTC)} UTC`;
}

/** What names a report: how it began, the schedule it was written under, and its window. */
export type ReportNaming = { trigger: ExecutiveReportTrigger; cadence: ExecutiveReportCadence; windowStart: string; windowEnd: string };

/** Whether a report is named by its week: a scheduled report written under the weekly schedule. */
function namedByWeek(report: ReportNaming): boolean {
  return report.trigger !== 'manual' && report.cadence === 'weekly';
}

/**
 * Which report this is, as a person names it (06-modules §5.3 R1: "which one … the name a person uses, not the primary
 * key"): a scheduled daily report by the day its window ends ("Tuesday 6 October 2026"), a scheduled weekly one by its week
 * ("Week to Monday 12 October 2026"), and a report "Refresh now" wrote by its part of the day, since a refreshed day is
 * read in parts that never overlap ("Tuesday 6 October 2026, 06:00 to 14:20 UTC"). The record's Window field states every
 * window in full.
 *
 * Why "Week to …" now (the owner's ruling of 2026-10-09 on ADR 0019's Open 43, "'Week to …' for weekly reports"): the report list's rows, the trend's
 * and "Compared with" carry the cadence (`ExecutiveReportRowDto`, `reviewing`), so a weekly report has ONE name by its week
 * on its record and in every list alike (06-modules §5.4, Identity) — never by a window's length, which does not tell one
 * (a scheduled window starts where the latest report ended, "Non-overlapping windows", and a missed tick stretches it).
 */
export function reportName(report: ReportNaming): string {
  if (report.trigger === 'manual') {
    const sameDay = new Date(report.windowStart).toISOString().slice(0, 10) === new Date(report.windowEnd).toISOString().slice(0, 10);
    if (sameDay) return `${longUtcDate(report.windowEnd)}, ${formatTime(report.windowStart, UTC)} to ${formatTime(report.windowEnd, UTC)} UTC`;
    return `${longUtcDate(report.windowEnd)}, from ${utcDateTime(report.windowStart)}`;
  }
  if (namedByWeek(report)) return `Week to ${longUtcDate(report.windowEnd)}`;
  return longUtcDate(report.windowEnd);
}

/**
 * A report named inside a sentence — "the report of Tuesday 6 October 2026", "the report of the week to Monday 12 October
 * 2026" — each a whole string with its date in place (05-content §8), never "the report of Week to …".
 */
export function reportInSentence(report: ReportNaming): string {
  return namedByWeek(report) ? `the report of the week to ${longUtcDate(report.windowEnd)}` : `the report of ${reportName(report)}`;
}

/** The browser tab's title for a report's own page: "Report of Tuesday 6 October 2026 · Calis", or of the week to a day. */
export function reportDocumentTitle(report: ReportNaming): string {
  return namedByWeek(report) ? `Report of the week to ${longUtcDate(report.windowEnd)} · Calis` : `Report of ${reportName(report)} · Calis`;
}

const PEOPLE = { one: '# person', other: '# people' } as const;
const MEETINGS = { one: '# meeting', other: '# meetings' } as const;

/** What a withheld report's words are chosen by: its two counts and the reason its executor recorded (null: none kept). */
export type WithheldFacts = Pick<ExecutiveInsufficientDto, 'people' | 'meetings' | 'reason'>;

/**
 * Whether a withheld report was held back by a minimum that no longer applies. Why by its counts, and only where it
 * recorded no reason (the owner's ruling of 2026-10-12, "Remove them everywhere": "A window with no material at all yields
 * no report or an explicit empty state … never a 'thin' state with a minimum in it"): since then a report is withheld only
 * when its window holds no meeting to write from, and the route serves zero and zero; a count above zero is a report a
 * build before the ruling withheld under the minimums then in force (E5), which stays readable until its retention — and,
 * since the owner's ruling of 2026-10-13 on ADR 0019's Open 50 ("Record the reasons"), a report withheld now records why,
 * so a recorded reason is never read as a minimum. Why "since removed" and never a date: the ruling is dated, but a build
 * before it serves until the deploy that brings it, so a date could be untrue of a report withheld in between.
 */
function heldBackByFormerMinimum(thin: WithheldFacts): boolean {
  return thin.reason === null && (thin.people > 0 || thin.meetings > 0);
}

/**
 * Why a withheld report was withheld, as its executor recorded it (the owner's ruling of 2026-10-13 on ADR 0019's Open 50,
 * "Record the reasons"; `EXECUTIVE_WITHHELD_REASONS`), each one complete sentence (05-content §8). Why these words:
 * - `no_meetings` — M1 found nothing it may write from: no summary completed inside the window, of a member whose
 *   summaries Calis makes, of a meeting no other report already read ("Read each meeting once"). Why "no new meeting
 *   summaries" and never "no meetings" (the review of 2026-10-12; 05-content §5, an empty state says why, truly): meetings
 *   may well have been held in such a window — their summaries completed later, read already, or of members without AI
 *   summaries.
 * - `contributors_lapsed` — every member whose meetings it read was struck at the after-gate: their admission or their AI
 *   summaries gone while the report was written (report-job.ts), so nothing read may be kept. Until this ruling the page
 *   said "no new meeting summaries" of it too, which was untrue: Open 50's first edge case.
 * - `sent_contributors_lapsed` — meetings of others were read, but the input budget had left every one of them out, so
 *   every meeting sent was a struck member's and the answer rests on nothing the report may keep.
 * "lost access to Calis or to AI summaries" is what the after-gate asks (`admit()`, and AI summaries of a member whose
 * summaries were read); "sent to the AI service" and "to keep it within its size" are the page's own words for the call and
 * the cut (`failureReason`, `leftOutSentence`).
 */
const WITHHELD_SENTENCES: Readonly<Record<ExecutiveWithheldReason, string>> = {
  no_meetings: 'No new meeting summaries to write from in this window, so no report was written.',
  contributors_lapsed: 'No report was written: every member whose meetings it read lost access to Calis or to AI summaries while it was being written, so nothing it read could be kept.',
  sent_contributors_lapsed:
    'No report was written: every member whose meetings were sent to the AI service lost access to Calis or to AI summaries while it was being written, and the other meetings had been left out to keep it within its size, so nothing sent could be kept.',
};

/**
 * A withheld report that recorded no reason and held back no counts. Why the rule and no cause (the owner's ruling of
 * 2026-10-13 on ADR 0019's Open 50, "Record the reasons" — "the two edge cases Open 50 named now read truly"): such a row was
 * withheld by a build that kept no reason, and its zero and zero do not tell the causes apart — no new summary in its
 * window (the build of 2026-10-12), every member read struck after the call (the same build), or, before 2026-10-12, a
 * window whose only meetings were the executives' own, stored then as zero people and zero meetings who were not
 * executives, held back by a minimum since removed (Open 50's second edge case). "No new meeting summaries" was untrue of
 * the last two. So it says what a report is written from, and that this one's reason was not recorded — never a cause.
 */
export const WITHHELD_NOT_RECORDED = 'A report is written from the new meeting summaries in its window; this one was withheld, and why was not recorded.';

/**
 * A withheld report's sentence: why it was withheld, as its executor recorded it (Open 50); for a report withheld before
 * the reason was kept, that a minimum since removed held back its meetings — exactly counted, no minimum named — where it
 * held back counts, else the rule with no cause claimed. Never "not enough material" and never "at least … are needed": no
 * minimum applies.
 */
export function thinSentence(thin: WithheldFacts): string {
  if (thin.reason !== null) return WITHHELD_SENTENCES[thin.reason];
  if (!heldBackByFormerMinimum(thin)) return WITHHELD_NOT_RECORDED;
  return `No report was written: a minimum since removed held back its ${pluralise(thin.meetings, MEETINGS)} of ${pluralise(thin.people, PEOPLE)}.`;
}

/** The qualifier for each recorded reason (Open 50): short, as a state's reason beside its word is (05-content §6). */
const WITHHELD_REASON_WORDS: Readonly<Record<ExecutiveWithheldReason, string>> = {
  no_meetings: 'No new meeting summaries',
  contributors_lapsed: 'Its members lost access',
  sent_contributors_lapsed: 'Its members lost access; the rest left out for size',
};

/**
 * The withheld report's state qualifier beside its Skipped pill, and the trend's note (05-content §6: a skipped run carries
 * its reason): the reason its executor recorded (Open 50, "Record the reasons"), or — withheld before the reason was kept —
 * a minimum since removed where it held back counts, else that its reason was not recorded, never a cause guessed.
 */
export function withheldReasonText(thin: WithheldFacts): string {
  if (thin.reason !== null) return WITHHELD_REASON_WORDS[thin.reason];
  return heldBackByFormerMinimum(thin) ? 'Held back by a minimum since removed' : 'Reason not recorded';
}

/** Why a written report counted no Slack climate, when Slack climate was off as it was written (Open 45, "Say why Slack wasn't counted"). */
export const SLACK_OFF_WHEN_WRITTEN = 'Not counted: Slack climate was off when the report was written';

/**
 * "11 Slack looks — 8 daily looks and 3 meeting checks — from 5 people", or why Slack was not counted, by the reason the
 * report's executor recorded (the owner's ruling of 2026-10-09 on ADR 0019's Open 45, "Say why Slack wasn't counted"; a
 * withheld report's too since his ruling of 2026-10-13 on Opens 50 and 54, "Record the reasons"): Slack climate off then; no
 * Slack climate in the window left to count — since 2026-10-12 one member's marks are counted ("Count whoever is
 * connected"), so this is the one reason a report with Slack climate on counts none, a mark another report already counted
 * ("Leave them out") included; or, on a report written before then, a minimum since removed, named with no number. A
 * report with no reason recorded reads "Not counted" alone, never a cause guessed from today's floor. Which reason applies
 * — never how many people there were.
 * Why "from any member connected to Slack", where it said "from a member whose summaries Calis makes" (the owner's ruling
 * of 2026-10-13 on ADR 0019's Open 56, "Count them too"): the report reads the Slack marks of every admitted member
 * connected to Slack under both Slack permissions, whether or not Calis makes their summaries, so no connected member's
 * climate is left out of what "none to count" covers.
 */
export function slackBasisText(slack: ExecutiveSlackBasisDto): string {
  if (slack.state === 'not_counted') {
    if (slack.reason === 'slack_off') return SLACK_OFF_WHEN_WRITTEN;
    if (slack.reason === 'no_marks') return 'Not counted: no Slack climate in this window to count from any member connected to Slack';
    if (slack.reason === 'below_minimum') return 'Not counted: a minimum since removed held it back';
    return 'Not counted';
  }
  return `${pluralise(slack.looks, { one: '# Slack look', other: '# Slack looks' })} — ${pluralise(slack.sweeps, { one: '# daily look', other: '# daily looks' })} and ${pluralise(slack.checks, { one: '# meeting check', other: '# meeting checks' })} — from ${pluralise(slack.people, PEOPLE)}`;
}

/**
 * A withheld report's written-from line for each recorded reason, and for one that recorded none: the line alone, and the
 * line with the Slack looks it counted all the same, by the plural rule — each one complete string with its time and count
 * in place (05-content §8), the reason as its sentence says it (`thinSentence`; Open 50, "Record the reasons").
 */
function withheldLines(when: string, reason: ExecutiveWithheldReason | null): { bare: string; one: string; other: string } {
  switch (reason) {
    case 'no_meetings':
      return {
        bare: `Withheld ${when}: no new meeting summaries in this window to write a report from.`,
        one: `Withheld ${when}: no new meeting summaries in this window to write a report from; its # Slack look was counted for the pulse.`,
        other: `Withheld ${when}: no new meeting summaries in this window to write a report from; its # Slack looks were counted for the pulse.`,
      };
    case 'contributors_lapsed':
      return {
        bare: `Withheld ${when}: every member whose meetings it read lost access while it was being written.`,
        one: `Withheld ${when}: every member whose meetings it read lost access while it was being written; its # Slack look was counted for the pulse.`,
        other: `Withheld ${when}: every member whose meetings it read lost access while it was being written; its # Slack looks were counted for the pulse.`,
      };
    case 'sent_contributors_lapsed':
      return {
        bare: `Withheld ${when}: every member whose meetings were sent lost access while it was being written, and the rest had been left out for size.`,
        one: `Withheld ${when}: every member whose meetings were sent lost access while it was being written, and the rest had been left out for size; its # Slack look was counted for the pulse.`,
        other: `Withheld ${when}: every member whose meetings were sent lost access while it was being written, and the rest had been left out for size; its # Slack looks were counted for the pulse.`,
      };
    case null:
    default:
      // Why no cause (Open 50's second edge case; `WITHHELD_NOT_RECORDED`): the reason was not kept, and zero and zero do not tell it.
      return {
        bare: `Withheld ${when}; why was not recorded.`,
        one: `Withheld ${when}; why was not recorded, and its # Slack look was counted for the pulse.`,
        other: `Withheld ${when}; why was not recorded, and its # Slack looks were counted for the pulse.`,
      };
  }
}

/**
 * The record's synchronised-from line (06-modules §5.3 R1: the source and when, never a figure presented as live): "Written
 * 6 Oct 2026, 06:04 UTC from 13 meetings of 9 people and 11 Slack looks." Each form one complete string with its counts;
 * Slack not counted is said with the reason the report recorded (Open 45, as `slackBasisText`), or none where it has none.
 * A withheld report says why it was withheld — the reason its executor recorded since the owner's ruling of 2026-10-13 on
 * Open 50, "Record the reasons" (`withheldLines`) — and, since his ruling of 2026-10-12, "Count whoever is connected", which
 * counts a window's Slack climate though it held no meeting, the Slack looks it counted all the same, which the route
 * serves as `insufficient.slack`.
 */
export function writtenFromSentence(report: Pick<ExecutiveReportDto, 'status' | 'completedAt' | 'createdAt' | 'basis' | 'insufficient'>): string {
  const when = utcDateTime(report.completedAt ?? report.createdAt);
  if (report.status === 'insufficient' || report.basis === null) {
    const thin = report.insufficient;
    if (thin !== null && heldBackByFormerMinimum(thin)) return `Withheld ${when}: a minimum since removed held it back.`;
    const lines = withheldLines(when, thin?.reason ?? null);
    return thin !== null && thin.slack.state === 'counted' ? pluralise(thin.slack.looks, { one: lines.one, other: lines.other }) : lines.bare;
  }
  const { meetings, people, slack } = report.basis;
  if (slack.state === 'not_counted') {
    if (slack.reason === 'slack_off') return `Written ${when} from ${pluralise(meetings, MEETINGS)} of ${pluralise(people, PEOPLE)}; Slack was not counted, as Slack climate was off.`;
    if (slack.reason === 'no_marks') return `Written ${when} from ${pluralise(meetings, MEETINGS)} of ${pluralise(people, PEOPLE)}; Slack was not counted, as this window held no Slack climate to count from any member connected to Slack.`;
    if (slack.reason === 'below_minimum') return `Written ${when} from ${pluralise(meetings, MEETINGS)} of ${pluralise(people, PEOPLE)}; Slack was not counted, as a minimum since removed held it back.`;
    return `Written ${when} from ${pluralise(meetings, MEETINGS)} of ${pluralise(people, PEOPLE)}; Slack was not counted.`;
  }
  return `Written ${when} from ${pluralise(meetings, MEETINGS)} of ${pluralise(people, PEOPLE)} and ${pluralise(slack.looks, { one: '# Slack look', other: '# Slack looks' })}.`;
}

/** "From 7 meetings of 4 people" — a theme's evidence, counts only (design §2.13). */
export function evidenceText(meetings: number, people: number): string {
  return `${pluralise(meetings, MEETINGS)} of ${pluralise(people, PEOPLE)}`;
}

/** The left-out note (design §2.6 `coverage_note`), said only when the input budget cut anything. */
export function leftOutSentence(coverage: { meetingsSent: number; meetingsLeftOut: number } | null): string | null {
  if (coverage === null || coverage.meetingsLeftOut === 0) return null;
  return pluralise(coverage.meetingsLeftOut, {
    one: 'The oldest meeting was left out to keep the report within its size.',
    other: 'The # oldest meetings were left out to keep the report within its size.',
  });
}

// --- Refresh now (the owner's pick E8; ADR 0019 "Non-overlapping windows", which superseded R3) --------------------------

/** Why never "replaces": since "Non-overlapping windows" a refresh adds a report of its own window and the latest stays. */
export const REFRESH_EXPLAINER = 'Refresh now writes a report of what came in since the latest one; the latest report stays as it is.';
export const REFRESH_QUEUED = 'A report of what came in since the latest one is being written.';

/** When "Refresh now" may be pressed again (the scope's cooldown and the day's cap, shared by every executive — Open 11). */
export function refreshAgainSentence(cooldownUntil: string, now: Date = new Date()): string {
  return `It can be refreshed again at ${utcMoment(cooldownUntil, now)}.`;
}

/**
 * Where the one-off re-summary stands, for the wait's words: `done` once none is to go and none is being re-summarised —
 * the server's own test of finished (chain.ts `firstReportWait`), whatever Coverage's state word, which says `paused`
 * first; `paused` while its operator holds it back (Coverage alone knows); `running` otherwise.
 */
export type ResummaryStage = 'running' | 'paused' | 'done';

export function resummaryStage(progress: ExecutiveBackfillProgressDto, paused = false): ResummaryStage {
  if (progress.toGo === 0 && progress.refreshing === 0) return 'done';
  return paused ? 'paused' : 'running';
}

/**
 * The first report's wait, as "Refresh now" answers it and Coverage shows it (the owner's rulings of 2026-10-08, "Wait for
 * the backfill", "Wait for the next report hour" and "Day counts from first due report": "a stated, not-yet state, not an
 * error"). Why by stage (the stage 6 review): once the re-summary is over the first report waits for the next report hour
 * alone, and `until` is exactly when it is written — not "at the latest", and not for the re-summary; while it runs, or is
 * held back, `until` is the report hour after the wait's one-day ceiling, which an earlier finish brings forward, so "at
 * the latest" is true. A null `until` means the wait's day has not begun — nobody marked and no report fallen due yet,
 * which the scheduler's next run ends — so its time is said to be not yet set, never tied to the re-summary beginning,
 * which a held-back re-summary never does.
 */
export function firstReportWaitSentence(until: string | null, stage: ResummaryStage, now: Date = new Date()): string {
  if (until === null) {
    return stage === 'paused'
      ? 'The one-off re-summary of members’ recent meetings is held back on this server; the first report’s time is not set yet.'
      : 'The first report waits for the one-off re-summary of members’ recent meetings; its time is not set yet.';
  }
  if (stage === 'done') return `The one-off re-summary of members’ recent meetings is over; the first report is written at ${utcMoment(until, now)}.`;
  if (stage === 'paused') return `The one-off re-summary of members’ recent meetings is held back on this server; the first report is written at ${utcMoment(until, now)} at the latest.`;
  return `The first report waits for the one-off re-summary of members’ recent meetings, and is written at ${utcMoment(until, now)} at the latest.`;
}

/**
 * Coverage's "Next scheduled report" (06-modules §6: the next scheduled run). Why not `nextScheduledAt` while the first
 * report waits (the stage 6 review): that is the due check's own instant — with no report yet, the moment of the read —
 * and never the wait, which is `firstReport.until` (ADR 0019: "`nextScheduledAt` stays the due check's own instant; the
 * wait is `firstReport`'s"); shown as the next report's time it contradicted the wait's banner above it.
 */
export function nextScheduledText(coverage: { nextScheduledAt: string; firstReport: { state: 'waiting'; until: string | null } | { state: 'released' } }, stage: ResummaryStage, now: Date = new Date()): string {
  if (coverage.firstReport.state === 'waiting') {
    const { until } = coverage.firstReport;
    if (until === null) return 'Not set yet: the first report waits for the one-off re-summary';
    return stage === 'done' ? utcMoment(until, now) : `${utcMoment(until, now)} at the latest`;
  }
  return utcMoment(coverage.nextScheduledAt, now);
}

/** The re-summary's progress in words, counts only (Coverage's `backfill`; never a name). */
export function backfillProgressSentence(progress: ExecutiveBackfillProgressDto): string {
  const done = Math.max(0, progress.marked - progress.refreshing);
  return `${pluralise(done, { one: '# member re-summarised', other: '# members re-summarised' })}, ${formatCount(progress.refreshing)} being re-summarised now, ${formatCount(progress.toGo)} still to start.`;
}

/** The share of members whose re-summary is over, for the determinate bar (03-components §12); null with nobody to do. */
export function backfillFraction(progress: ExecutiveBackfillProgressDto): number | null {
  const total = progress.marked + progress.toGo;
  if (total === 0) return null;
  return Math.max(0, progress.marked - progress.refreshing) / total;
}

/** The answer to a press, in the toast's words: queued, or the stated wait. */
export function refreshAnswerSentence(answer: ExecutiveRefreshResponse, now: Date = new Date()): string {
  if (answer.state === 'queued') return REFRESH_QUEUED;
  // Why the stage from the counts alone: the press's answer says nothing of a held-back re-summary (Coverage does).
  return `${firstReportWaitSentence(answer.until, resummaryStage(answer.backfill), now)} ${backfillProgressSentence(answer.backfill)}`;
}

/**
 * "No report yet" (design §2.13), and when the first comes. Why the wait and its time now (the owner's ruling of 2026-10-09
 * on ADR 0019's Open 44, "First report's time shown"): the report's route carries the first report's wait (`firstReport`), so the Report section
 * and the Overview card say it as Coverage's banner does — by where the re-summary stands, with its time
 * (`firstReportWaitSentence`). Never `nextScheduledAt`, which with no report yet is the due check's own instant — the moment
 * of the read, "due at once with none yet" — a minute already past (the stage 6 review). Once the wait is over with still no
 * report (due at once, or after a failed one until the next report hour) no time is named: the schedule writes it, or
 * Refresh now, and Coverage shows when. While the first report is being written, that is said instead.
 */
export const NO_REPORT_TITLE = 'No report yet';
export const FIRST_REPORT_WRITING = 'The first is being written now.';
export const FIRST_REPORT_RELEASED = 'The first is written on the schedule, or with Refresh now; Coverage shows when.';
export function noReportDescription(running: boolean, firstReport: ExecutiveReportResponse['firstReport'], now: Date = new Date()): string {
  if (running) return FIRST_REPORT_WRITING;
  if (firstReport.state === 'waiting') return firstReportWaitSentence(firstReport.until, firstReport.resummary, now);
  return FIRST_REPORT_RELEASED;
}

/** A report being written now, said with the §6 word for where it stands (Pending or Running) and how it began. */
export function runningSentence(running: { trigger: ExecutiveReportTrigger; status: 'queued' | 'running'; since: string }, now: Date = new Date()): string {
  const began = running.trigger === 'manual' ? 'by hand' : 'by the schedule';
  return running.status === 'queued'
    ? `A report is waiting to be written, started ${began} at ${utcMoment(running.since, now)}.`
    : `A report is being written now, started ${began} at ${utcMoment(running.since, now)}.`;
}

/** The state word for a running report: 05-content §6's Pending or Running. */
export function runningWord(status: 'queued' | 'running'): 'Pending' | 'Running' {
  return status === 'queued' ? 'Pending' : 'Running';
}

/**
 * Why a failed report failed, in a reader's words (05-content §4: what happened and why), from the closed codes a failed
 * report row carries (report-job.ts: the provider's, the gate's and admission's refusals, and the executor's own). Any
 * other code reads as a fault inside Calis — never the code itself.
 */
const FAILURE_REASONS: Readonly<Record<string, string>> = {
  ai_unavailable: 'the AI service did not respond',
  ai_invalid_output: 'the AI service returned an answer Calis could not use',
  ai_not_configured: 'the AI service is not configured on this server',
  executive_off: 'executive intelligence was switched off on this server',
  executive_not_granted: 'the executive it was written for no longer had access',
  no_scope_access: 'the executive it was written for no longer had access',
  not_approved: 'the executive it was written for no longer had access',
  no_subject: 'the executive it was written for no longer had access',
  session_revoked: 'the executive it was written for no longer had access',
  executive_no_reader: 'nobody could read it any more',
};

export function failureReason(code: string): string {
  return Object.prototype.hasOwnProperty.call(FAILURE_REASONS, code) ? FAILURE_REASONS[code]! : 'something failed inside Calis';
}

/**
 * Which page a failure banner sits on: the latest report (`latest`), one opened by its id (`byId`), or none written yet
 * (`none`). Why it matters (the stage 6 review): the route sends `lastFailure` whenever a failure is newer than the report
 * shown — with no report, and on an earlier report opened by its id, too — so "the report shown is the latest one written"
 * is true of the latest report's page alone.
 */
export type FailureContext = 'latest' | 'byId' | 'none';

export function lastFailureTitle(context: FailureContext): string {
  if (context === 'byId') return 'A later report failed';
  if (context === 'none') return 'A report failed';
  return 'The latest report failed';
}

export function lastFailureSentence(failure: { code: string; at: string }, context: FailureContext, now: Date = new Date()): string {
  const when = utcMoment(failure.at, now);
  const why = failureReason(failure.code);
  if (context === 'byId') return `A report after this one failed at ${when}, because ${why}.`;
  if (context === 'none') return `A report failed at ${when}, because ${why}; none has been written yet. The next is written at the next report hour, or with Refresh now.`;
  return `The latest report failed at ${when}, because ${why}; the report shown is the latest one written. The next is written at the next report hour, or with Refresh now.`;
}

// --- Pulse (E12) ----------------------------------------------------------------------------------------------------

/**
 * A report's own count of a warning signal, exact — a one or a two included since the owner's ruling of 2026-10-12, "Exact
 * counts everywhere", which ended the route's `'rare'` — or "Not counted" where the report holds no climate count.
 */
export function signalTodayText(count: ExecutiveSignalCount | null): string {
  return count === null ? 'Not counted' : formatCount(count);
}

/** A previous week's total of a signal, exact (the same ruling, which ended a week's `'rare'`), or a dash where none was counted. */
export function signalWeekText(count: ExecutiveWeekSignalCount | null): string {
  return count === null ? EM_DASH : formatCount(count);
}

/** Sorting a report's signal count by magnitude; not counted sorts last. */
export function signalTodaySortValue(count: ExecutiveSignalCount | null): number | null {
  return count;
}

/** Sorting a week's total by magnitude; none counted sorts last. */
export function signalWeekSortValue(count: ExecutiveWeekSignalCount | null): number | null {
  return count;
}

export const SIGNALS_NOTE = 'Each count is how many of the report’s meetings raised the signal; the previous week’s total adds up that week’s reports.';

/** "29 Sep to 5 Oct 2026" — the previous calendar week, Monday to Sunday (its end is the next Monday 00:00, exclusive). */
export function weekText(weekStart: string, weekEnd: string): string {
  const lastDay = new Date(Date.parse(weekEnd) - 1);
  return `${formatDate(weekStart, UTC)} to ${formatDate(lastDay, UTC)}`;
}

/**
 * A tile's comparison basis (04-data §3: "The comparison basis MUST be stated"): the previous week's total, or that none
 * was counted. Why "not counted" and never "no report" (the stage 6 review): the route's null means no report of that week
 * carried a counted climate — a week of withheld reports, or of reports none of whose meetings carried a climate word (or
 * Slack mark), reads null as surely as a week with no report at all.
 */
export function previousWeekHint(count: number | null): string {
  if (count === null) return 'Not counted in the previous week';
  return `${formatCount(count)} in the previous week`;
}

/**
 * A written report that holds no climate count (the route's `not_counted`), said by the reason its executor recorded (the
 * owner's ruling of 2026-10-13 on ADR 0019's Open 54, "Record the reasons": "a closed reason for an uncounted climate"):
 * `no_words` — no meeting it was written from carried a climate word (a summary made while executive intelligence was off,
 * or whose word slipped), the exact cause. A report written before the reason was kept recorded none: since the owner's
 * ruling of 2026-10-12 none of its meetings carried a climate word, but one written before then may instead have been held
 * under the climate's own minimum, since removed. Why the rule and no cause for it (as `SLACK_NOT_COUNTED_RULE` for a Slack
 * reason not recorded): the route cannot tell the two apart, and a cause stated would be untrue of one of them — while
 * naming the old minimum on every report written since would put back the very minimum the ruling removed. The rule as it
 * stands now, and that this report counted none, are true of both.
 */
export const CLIMATE_NOT_COUNTED_TITLE = 'Climate not counted';
export const CLIMATE_NOT_COUNTED = 'The climate is counted from every meeting whose summary carries a climate word; this report counted none.';
export const CLIMATE_NO_WORDS = 'No meeting this report was written from carried a climate word, so its climate was not counted.';

/** Why a written report counted no climate: the cause it recorded (Open 54), or, where it recorded none, the rule. */
export function climateNotCountedSentence(reason: ExecutiveClimateUncounted | null): string {
  return reason === 'no_words' ? CLIMATE_NO_WORDS : CLIMATE_NOT_COUNTED;
}

/**
 * Whose Slack the report counts, said where its rule is stated (Coverage, and the Pulse where a report recorded no reason).
 * Why every member connected to Slack under both Slack permissions, "whether or not Calis makes their summaries", where it
 * said "every connected member whose summaries Calis makes" (the owner's ruling of 2026-10-13 on ADR 0019's Open 56, "Count
 * them too"): the report reads the Slack marks of every admitted member who holds both Slack grants and a live connection,
 * AI summaries or not — the gate the Slack check and the daily look ask themselves (`slackAnalysisRefusal`) — so the words
 * name exactly that, and say outright that AI summaries no longer decide it. "Permissions" is the members' own word for the
 * grants (Settings → Your data).
 */
export const SLACK_CLIMATE_ON = 'On — counted from the Slack looks of every member connected to Slack with both Slack permissions, whether or not Calis makes their summaries';

/**
 * Why the Pulse's Slack tiles are absent (`slack.state === 'not_counted'`), by the reason the report recorded (the owner's
 * ruling of 2026-10-09 on ADR 0019's Open 45, "Say why Slack wasn't counted"; a withheld report's too since his ruling of
 * 2026-10-13 on Opens 50 and 54, "Record the reasons"): Slack climate off when the report was written; no Slack climate in
 * its window left to count (since 2026-10-12 one member's marks are counted — "Count whoever is connected" — and since
 * 2026-10-13 every connected member's with both Slack permissions, AI summaries or not — "Count them too", Open 56); or, on a
 * report written before then, a minimum since removed, with no number. Where the report recorded no reason the page
 * states the rule Slack's climate is counted under and claims no cause. Never a count.
 */
export const SLACK_NOT_COUNTED_TITLE = 'Slack was not counted';
export const SLACK_OFF_PULSE = 'Slack climate was off on this server when the report was written.';
export const SLACK_NO_MARKS_PULSE = 'This report’s window held no Slack climate to count from any member connected to Slack.';
export const SLACK_BELOW_FORMER_MINIMUM_PULSE = 'A minimum since removed held this report’s Slack climate back.';
/** The rule, for a report that recorded no reason (one a build before Open 45's record — or, withheld, before Open 54's — wrote). */
export const SLACK_NOT_COUNTED_RULE = 'Slack’s climate is counted while Slack climate is on, from the Slack looks of every member connected to Slack with both Slack permissions, whether or not Calis makes their summaries.';

/**
 * Coverage's Slack line for the last report (06-modules §6: the questions answered on the screen): its Slack climate where
 * any member's marks were counted — a withheld report's too since the owner's ruling of 2026-10-12, "Count whoever is
 * connected" — else the reason its executor recorded (Open 45, as the report's own basis says it; a withheld report's since
 * his ruling of 2026-10-13 on Open 54, "Record the reasons", from its own column). For a report with no reason recorded —
 * one a build before the record wrote, written or withheld — Coverage says what holds now: Slack climate off on this
 * server, or, with it on, "Not counted" alone. Why no cause then (the owner's ruling of 2026-10-12): such a report may have
 * been written under a Slack minimum since removed, or withheld by a build that counted no Slack for a withheld window, so
 * "no Slack climate in this window" could be untrue of it, and a minimum is never inferred.
 */
export function coverageSlackText(last: { status: 'ready' | 'insufficient'; slack: ExecutiveSlackBasisDto }, slackClimate: 'on' | 'off'): string {
  if (last.slack.state === 'counted') return slackBasisText(last.slack);
  if (last.slack.reason !== null) return slackBasisText(last.slack);
  if (slackClimate === 'off') return 'Not counted: Slack climate is off on this server';
  return 'Not counted';
}

/** A trend row's top signals (the most counted, at most three), or the dash. */
export function topSignalsText(signals: readonly ClimateSignal[]): string {
  return signals.length === 0 ? EM_DASH : formatList(signals.map((signal) => CLIMATE_SIGNAL_LABELS[signal]));
}

/** The Monday (UTC) a report's window end falls in, as "28 Sep 2026" — the trend's week. */
export function weekOf(windowEnd: string): string {
  return formatDate(weekStartOf(windowEnd), UTC);
}

/** The Monday 00:00 UTC of the week an instant falls in, as an ISO string (for sorting). */
export function weekStartOf(instant: string): string {
  const date = new Date(instant);
  const day = (date.getUTCDay() + 6) % 7;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - day)).toISOString();
}

/**
 * How a trend row reads where its eight counts are absent: a withheld report, by why it was withheld (as its record's pill
 * says it, `withheldReasonText`), or a written one with no climate count — with the cause it recorded where it recorded one
 * (the owner's rulings of 2026-10-13 on ADR 0019's Opens 50 and 54, "Record the reasons": the trend states the cause, not
 * the rule), else the title alone. Why neither says "not enough" (the owner's ruling of 2026-10-12, "Remove them
 * everywhere"): no minimum applies.
 */
export function trendRowNote(row: Pick<ExecutiveReportRowDto, 'status' | 'climate' | 'people' | 'meetings' | 'withheldReason' | 'climateUncounted'>): string | null {
  if (row.status === 'insufficient') return withheldReasonText({ people: row.people, meetings: row.meetings, reason: row.withheldReason });
  if (row.climate === null) return row.climateUncounted === 'no_words' ? 'Climate not counted: no meeting carried a climate word' : CLIMATE_NOT_COUNTED_TITLE;
  return null;
}

/** Each recorded Slack reason as the trend's short note (Open 45's three, as `slackBasisText` says them in full). */
const TREND_SLACK_NOTES: Readonly<Record<ExecutiveSlackUncounted, string>> = {
  slack_off: 'Slack not counted: Slack climate was off',
  no_marks: 'Slack not counted: no Slack climate to count',
  below_minimum: 'Slack not counted: a minimum since removed',
};

/**
 * Why a trend row's Slack counts are absent, where its report recorded why (the owner's rulings of 2026-10-13 on ADR 0019's
 * Opens 50 and 54, "Record the reasons": a withheld report's reason too, from its own column); null where Slack was
 * counted, or where no reason was recorded — the dash then says a count not made, and no cause is claimed.
 */
export function trendSlackNote(row: Pick<ExecutiveReportRowDto, 'slackClimate' | 'slackUncounted'>): string | null {
  if (row.slackClimate !== null || row.slackUncounted === null) return null;
  return TREND_SLACK_NOTES[row.slackUncounted];
}

// --- Matters (E10) --------------------------------------------------------------------------------------------------

export const MATTER_STATUS_FILTERS = ['open', 'done', 'dismissed', 'all'] as const;
export type MatterStatusFilter = (typeof MATTER_STATUS_FILTERS)[number];
export const MATTER_FILTER_LABELS: Readonly<Record<MatterStatusFilter, string>> = { open: 'Open', done: 'Completed', dismissed: 'Dismissed', all: 'All' };

export function parseMatterFilter(raw: string | null): MatterStatusFilter {
  return (MATTER_STATUS_FILTERS as readonly string[]).includes(raw ?? '') ? (raw as MatterStatusFilter) : 'open';
}

/**
 * Where a matter came from (design §2.13: "Report of 5 Oct" or "Added by <name>"). Why a weekly report's matter reads
 * "Report of the week to Monday 12 October 2026" (the owner's ruling of 2026-10-09 on ADR 0019's Open 43, "a weekly report is
 * named 'Week to 12 Oct' everywhere"; the Phase 2a review found this list still naming it by the day it was written): the
 * report has one name by its week on its page and in every list, and this cell names the same report — a whole string
 * with its date in place, as `reportDocumentTitle` says it (05-content §8).
 */
export function matterFromText(matter: Pick<ExecutiveMatterDto, 'from' | 'createdAt'>): string {
  if (matter.from.kind === 'report') {
    if (!matter.from.reportReadable) return 'A report no longer kept';
    const report = matter.from.report;
    return report !== null && namedByWeek(report) ? `Report of the week to ${longUtcDate(report.windowEnd)}` : `Report of ${formatDate(matter.createdAt, UTC)}`;
  }
  if (matter.from.byYou) return 'Added by you';
  // Why no name for a creator who no longer holds the capability: the route names only the executives who may read now.
  return matter.from.addedBy === null ? 'Added by an executive' : `Added by ${matter.from.addedBy}`;
}

/**
 * How the From column sorts (04-data §1.2.1: "Sorting MUST be type-aware … dates chronologically"; the stage 6 review found
 * its words sorted as text, "Report of 1 Oct" before "Report of 29 Sep"): a report's matters first, by the instant the
 * report wrote them (its date, as the cell says it), then the matters executives added, by the words the cell shows. The
 * ISO instant after its group's mark sorts chronologically under the table's numeric collation.
 */
export function matterFromSortValue(matter: Pick<ExecutiveMatterDto, 'from' | 'createdAt'>): string {
  return matter.from.kind === 'report' ? `0 ${matter.createdAt}` : `1 ${matterFromText(matter)}`;
}

/** "1 of 2" — how many of the executives who may read now marked it completed (the route counts them now). */
export function completedByText(doneBy: ExecutiveMatterDto['doneBy']): string {
  return `${formatCount(doneBy.count)} of ${formatCount(doneBy.of)}`;
}

/** The names behind "1 of 2", said on expansion: "Completed by Priya Raman." — or that nobody has yet. */
export function completedByNamesSentence(doneBy: ExecutiveMatterDto['doneBy']): string {
  if (doneBy.names.length === 0) return 'No executive has marked it completed yet.';
  return `Completed by ${formatList(doneBy.names)}.`;
}

/** The toast after an executive's own change (each executive's word is their own; nobody's changes another's). */
export function matterStatusToast(text: string, next: ExecutiveMatterStatus, previous: ExecutiveMatterStatus): string {
  if (next === 'done') return `“${text}” marked as completed for you.`;
  if (next === 'dismissed') return `“${text}” dismissed for you.`;
  return previous === 'done' ? `“${text}” reopened for you.` : `“${text}” restored for you.`;
}

/**
 * The list's empty states. Every filter but All narrows the list, so its empty state says so and is offered with "Clear
 * filters", which shows All (05-content §5: "A filtered empty state MUST say so and offer to clear the filter"; 04-data §1.3).
 */
export function matterEmpty(filter: MatterStatusFilter): { title: string; description: string } {
  switch (filter) {
    case 'done':
      return { title: 'No matter marked completed', description: 'Only the matters you marked as completed are shown; each executive marks their own.' };
    case 'dismissed':
      return { title: 'No matter dismissed', description: 'Only the matters you dismissed are shown; a dismissed matter can be restored.' };
    case 'all':
      return { title: 'No matters yet', description: 'Each report’s suggested actions, and the matters executives add, appear here.' };
    case 'open':
    default:
      return { title: 'No open matters', description: 'Only the matters open for you are shown; each report’s suggested actions, and the matters executives add, appear here.' };
  }
}

export const MATTER_TEXT_MAX = 300;
export const ADD_MATTER_DESCRIPTION = 'Every executive sees it on the shared list; only you can change its words or delete it.';
export const EDIT_MATTER_DESCRIPTION = 'Any executive can change the priority and due date; every change is recorded with who made it.';
export const MATTER_TEXT_LOCKED = 'Only the executive who added this matter can change its words.';

/**
 * How much of a paged list of reports is on screen (06-modules §5.3 R3: never a subset presented as the whole; 04-data
 * §1.2.1: a sort covers every row, so the remaining pages load before any other order is drawn).
 */
export function reportsExtentText(count: number, more: boolean): string {
  if (more) return pluralise(count, { one: 'Showing the most recent report; earlier ones load on request.', other: 'Showing the # most recent reports; earlier ones load on request.' });
  return pluralise(count, { zero: 'No report is kept yet.', one: 'Showing the one report kept.', other: 'Showing all # reports kept.' });
}

export const LOADING_ALL_REPORTS = 'Loading every report so the sort covers them all…';

/**
 * The badge's announced words (02-patterns §1.4's count, said beside the destination's name). Why two forms (the stage 6
 * review; 05-content: say what is true): an executive who has opened no report — newly granted, say, with months of
 * reports kept — has no "last opened one", and is told how many they have not opened instead.
 */
export function newReportsLabel(count: number, noneOpened = false): string {
  if (noneOpened) {
    if (count > 99) return 'More than 99 reports you have not opened';
    return pluralise(count, { one: '# report you have not opened', other: '# reports you have not opened' });
  }
  if (count > 99) return 'More than 99 new reports since you last opened one';
  return pluralise(count, { one: '# new report since you last opened one', other: '# new reports since you last opened one' });
}

/** Said where a written report suggested no action (06-modules §5.8: "Empty applies per region"; R3 says so in place). */
export const NO_SUGGESTED_ACTIONS = 'This report suggested no actions.';

/** "N open matters" on the Overview card (design §2.13). */
export function openMattersText(count: number): string {
  return pluralise(count, { zero: 'No open matters', one: '# open matter', other: '# open matters' });
}
